import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Create axios instance with withCredentials for cookie support
const axiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

// Add response interceptor to handle 401 errors globally
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Check if this is a trophy-related endpoint (optional feature)
      const isTrophyEndpoint = error.config?.url?.includes('/trophies');
      
      if (!isTrophyEndpoint) {
        // 401 means session invalid - force reload to trigger login
        console.log('401 Unauthorized - Session invalid, reloading...');
        // Small delay to ensure cookie is cleared
        setTimeout(() => {
          window.location.href = window.location.origin;
        }, 100);
      } else {
        // Trophy endpoints are optional, just log and continue
        console.log('Trophy endpoint requires authentication - feature unavailable');
      }
    }
    return Promise.reject(error);
  }
);

// Create axios instance for admin panel
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

export default api;

// Profile API
export const profileAPI = {
  getAll: async () => {
    const response = await axiosInstance.get('/profiles');
    return response.data;
  },
  
  create: async (name, avatar) => {
    const response = await axiosInstance.post('/profiles', {
      name,
      avatar,
      streak: 0,
      totalPoints: 0
    });
    return response.data;
  },
  
  getById: async (profileId) => {
    const response = await axiosInstance.get(`/profiles/${profileId}`);
    return response.data;
  },
  
  update: async (profileId, { name, streak, totalPoints }) => {
    const params = {};
    if (name !== undefined) params.name = name;
    if (streak !== undefined) params.streak = streak;
    if (totalPoints !== undefined) params.totalPoints = totalPoints;
    
    const response = await axiosInstance.put(`/profiles/${profileId}`, null, { params });
    return response.data;
  },
  
  deleteProfile: async (profileId) => {
    const response = await axiosInstance.delete(`/profiles/${profileId}`);
    return response.data;
  },
  
  setProfile: async (profileId) => {
    const response = await axiosInstance.post('/auth/set-profile', { profile_id: profileId });
    return response.data;
  }
};

// Passage API
export const passageAPI = {
  getAll: async () => {
    const response = await axiosInstance.get('/passages');
    return response.data;
  },
  
  getById: async (passageId) => {
    const response = await axiosInstance.get(`/passages/${passageId}`);
    return response.data;
  },
  
  getLessons: async (passageId) => {
    const response = await axiosInstance.get(`/passages/${passageId}/lessons`);
    return response.data;
  }
};

// Lesson API
export const lessonAPI = {
  getById: async (lessonId) => {
    const response = await axiosInstance.get(`/lessons/${lessonId}`);
    return response.data;
  },
  
  validateActivity: async (lessonId, activityIndex, userAnswers) => {
    const response = await axiosInstance.post('/lessons/validate-activity', {
      lesson_id: lessonId,
      activity_index: activityIndex,
      user_answers: userAnswers
    });
    return response.data;
  }
};

// Progress API
export const progressAPI = {
  get: async (profileId, passageId) => {
    const response = await axiosInstance.get(`/progress/${profileId}/${passageId}`);
    return response.data;
  },
  
  update: async (profileId, passageId, updateData) => {
    const response = await axiosInstance.post(`/progress/${profileId}/${passageId}/update`, updateData);
    return response.data;
  },
  
  completeLesson: async (profileId, passageId, dayNumber, earnedPoints) => {
    const response = await axiosInstance.post(`/progress/${profileId}/${passageId}/complete-lesson`, {
      day_number: dayNumber,
      earned_points: earnedPoints
    });
    return response.data;
  }
};

// Leaderboard API
export const leaderboardAPI = {
  getSessionLeaderboard: async (sessionId) => {
    const response = await axiosInstance.get(`/leaderboards/session/${sessionId}`);
    return response.data;
  },
  
  getLessonLeaderboard: async (lessonId) => {
    const response = await axiosInstance.get(`/leaderboards/lesson/${lessonId}`);
    return response.data;
  },
  
  getProfileSessionPoints: async (sessionId, profileId) => {
    const response = await axiosInstance.get(`/leaderboards/session/${sessionId}/profile/${profileId}`);
    return response.data;
  },
  
  getProfileLessonPoints: async (lessonId, profileId) => {
    const response = await axiosInstance.get(`/leaderboards/lesson/${lessonId}/profile/${profileId}`);
    return response.data;
  },
  
  getTotalPointsLeaderboard: async () => {
    const response = await axiosInstance.get('/leaderboards/total-points');
    return response.data;
  }
};

